import React, { useEffect, useMemo, useState } from "react";
import { Modal, Checkbox, Button, Select, notification, Spin, Input } from "antd";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  bulkCreateSafetyQuestions,
  fetchSafetyQuestions,
} from "../../services/safetyAPI";
import { useAuth } from "../../hooks/useAuth";
import queryClient from "../../queryClient";

const getQuestionId = (q) => q.id || q._id;

const CopySafetyQuestionsFromProjectModal = ({ visible, onClose }) => {
  const { selectedProject, currentUser } = useAuth();
  const projectId = selectedProject?.project_id;
  const [sourceProjectId, setSourceProjectId] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [searchText, setSearchText] = useState("");

  const sourceProjects = (currentUser?.projects || []).filter(
    (p) => p.project_id !== projectId
  );

  const { data: targetData } = useQuery({
    queryKey: ["safetyQuestions", projectId],
    queryFn: () => fetchSafetyQuestions(projectId),
    enabled: visible && !!projectId,
  });

  const { data: sourceData, isLoading } = useQuery({
    queryKey: ["safetyQuestions", sourceProjectId],
    queryFn: () => fetchSafetyQuestions(sourceProjectId),
    enabled: visible && !!sourceProjectId,
  });

  const existingKeys = useMemo(() => {
    return new Set(
      (targetData?.questions || []).map(
        (q) =>
          `${(q.category || "").trim().toLowerCase()}||${(q.text || "")
            .trim()
            .toLowerCase()}`
      )
    );
  }, [targetData]);

  const allQuestions = useMemo(() => {
    return (sourceData?.questions || []).map((question) => {
      const key = `${(question.category || "").trim().toLowerCase()}||${(
        question.text || ""
      )
        .trim()
        .toLowerCase()}`;
      return {
        ...question,
        id: getQuestionId(question),
        is_already_added: existingKeys.has(key),
      };
    });
  }, [sourceData, existingKeys]);

  const filteredQuestions = useMemo(() => {
    if (!searchText) return allQuestions;
    const searchLower = searchText.toLowerCase();
    return allQuestions.filter(
      (q) =>
        q.category?.toLowerCase().includes(searchLower) ||
        q.text?.toLowerCase().includes(searchLower)
    );
  }, [allQuestions, searchText]);

  const questionsToCopy = useMemo(() => {
    return selectedIds
      .map((id) => allQuestions.find((q) => q.id === id))
      .filter((q) => q && !q.is_already_added);
  }, [selectedIds, allQuestions]);

  useEffect(() => {
    setSelectedIds([]);
    setSearchText("");
  }, [sourceProjectId]);

  useEffect(() => {
    if (!visible) {
      setSourceProjectId(null);
      setSelectedIds([]);
      setSearchText("");
    }
  }, [visible]);

  const copyMutation = useMutation({
    mutationFn: (questions) =>
      bulkCreateSafetyQuestions({
        projectId,
        questions: questions.map((q) => ({
          text: q.text,
          category: q.category || "",
        })),
      }),
    onSuccess: (data) => {
      notification.success({
        message: "Safety questions copied successfully",
        description:
          data.detail || `Successfully copied ${data.added} question(s).`,
      });
      queryClient.invalidateQueries({
        queryKey: ["safetyQuestions", projectId],
      });
      handleClose();
    },
    onError: (error) => {
      const errorDetail = error.response?.data?.detail;
      notification.error({
        message: "Failed to copy safety questions",
        description:
          typeof errorDetail === "string"
            ? errorDetail
            : errorDetail?.message || error.message || "Unknown error",
      });
    },
  });

  const handleSelectAll = () => {
    const availableIds = filteredQuestions
      .filter((q) => !q.is_already_added)
      .map((q) => q.id);
    const allSelected =
      availableIds.length > 0 &&
      availableIds.every((id) => selectedIds.includes(id));

    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !availableIds.includes(id)));
    } else {
      setSelectedIds((prev) => [...new Set([...prev, ...availableIds])]);
    }
  };

  const handleToggle = (questionId) => {
    const question = allQuestions.find((q) => q.id === questionId);
    if (question?.is_already_added) return;
    setSelectedIds((prev) =>
      prev.includes(questionId)
        ? prev.filter((id) => id !== questionId)
        : [...prev, questionId]
    );
  };

  const handleCopy = () => {
    if (!sourceProjectId) {
      notification.warning({
        message: "No project selected",
        description: "Please select a source project.",
      });
      return;
    }
    if (questionsToCopy.length === 0) {
      notification.warning({
        message: "No questions selected",
        description: "Please select at least one question that hasn't been added yet.",
      });
      return;
    }
    copyMutation.mutate(questionsToCopy);
  };

  const handleClose = () => {
    setSourceProjectId(null);
    setSelectedIds([]);
    setSearchText("");
    onClose();
  };

  if (!selectedProject || !projectId) return null;

  return (
    <Modal
      title="Copy Safety Questions From a Source Project"
      open={visible}
      onCancel={handleClose}
      footer={null}
      centered
      width={700}
      destroyOnClose
      zIndex={1100}
    >
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Source Project
        </label>
        {sourceProjects.length === 0 ? (
          <p className="text-gray-500 text-sm">
            You need at least one other project to copy safety questions from.
          </p>
        ) : (
          <Select
            placeholder="Select a project"
            value={sourceProjectId || undefined}
            onChange={setSourceProjectId}
            style={{ width: "100%" }}
            options={sourceProjects.map((p) => ({
              value: p.project_id,
              label: p.project_name || p.name || p.project_id,
            }))}
          />
        )}
      </div>

      {sourceProjectId && (
        <>
          <div className="mb-4">
            <div className="flex justify-start items-center mb-3">
              <Button type="link" onClick={handleSelectAll} style={{ padding: 0 }}>
                {filteredQuestions.filter((q) => !q.is_already_added).length > 0 &&
                filteredQuestions
                  .filter((q) => !q.is_already_added)
                  .every((q) => selectedIds.includes(q.id))
                  ? "Deselect All"
                  : "Select All"}
              </Button>
            </div>
            <Input.Search
              placeholder="Search by category or question..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </div>

          <div
            style={{
              maxHeight: "400px",
              overflowY: "auto",
              border: "1px solid #d9d9d9",
              borderRadius: "4px",
              padding: "8px 12px",
            }}
          >
            {isLoading ? (
              <div className="flex justify-center py-8">
                <Spin />
              </div>
            ) : filteredQuestions.length === 0 ? (
              <p className="text-gray-500 text-sm py-4 text-center">
                No questions found in the source project.
              </p>
            ) : (
              filteredQuestions.map((question) => (
                <div
                  key={question.id}
                  style={{
                    padding: "8px 0",
                    borderBottom: "1px solid #f0f0f0",
                    opacity: question.is_already_added ? 0.5 : 1,
                  }}
                >
                  <Checkbox
                    checked={
                      question.is_already_added ||
                      selectedIds.includes(question.id)
                    }
                    disabled={question.is_already_added}
                    onChange={() => handleToggle(question.id)}
                  >
                    <div>
                      {question.category ? (
                        <div className="text-xs text-gray-500">
                          {question.category}
                        </div>
                      ) : null}
                      <div>{question.text}</div>
                      {question.is_already_added ? (
                        <div className="text-xs text-gray-400">Already added</div>
                      ) : null}
                    </div>
                  </Checkbox>
                </div>
              ))
            )}
          </div>

          <div className="flex justify-end mt-4 gap-2">
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              type="primary"
              onClick={handleCopy}
              loading={copyMutation.isPending}
              disabled={questionsToCopy.length === 0}
            >
              Copy {questionsToCopy.length > 0 ? `(${questionsToCopy.length})` : ""}
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
};

export default CopySafetyQuestionsFromProjectModal;
